import { useI18n } from "@/context/I18nContext";

function Dot({ ok, warn }) {
  const color = ok ? "var(--mq-green)" : warn ? "var(--mq-yellow)" : "var(--mq-red)";
  return <span className="mq-status-dot" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />;
}

function Row({ label, ok, warn, detail, testid }) {
  const { t } = useI18n();
  return (
    <div className="flex items-center justify-between py-3 border-b last:border-0" data-testid={testid} style={{ borderColor: "var(--mq-border)" }}>
      <div className="flex items-center gap-3">
        <Dot ok={ok} warn={warn} />
        <span className="text-sm">{label}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-[var(--mq-text-2)]">{detail}</span>
        <span
          className="mq-label"
          style={{ color: ok ? "var(--mq-green)" : warn ? "var(--mq-yellow)" : "var(--mq-red)" }}
        >
          {ok ? t("pass") : warn ? t("warn") : t("fail")}
        </span>
      </div>
    </div>
  );
}

export default function CompliancePanel({ state }) {
  const { t } = useI18n();
  const c = state?.compliance || {};
  const th = state?.compliance_thresholds || {};
  const spread = c.spread_pct ?? 0;
  const dBid = c.depth_bid_usd ?? 0;
  const dAsk = c.depth_ask_usd ?? 0;
  const vol = c.volume_24h_usd ?? 0;

  // Phase E — while the bot is "warming up" (first N hours since start)
  // we downgrade depth/volume breaches from FAIL to WARN so the operator
  // is not alarmed by ramps that are normal during liquidity build-up.
  // Spread is NOT downgraded — a wide spread is always a real problem.
  const warmingUp = !!state?.compliance_warming_up;
  const depthOkOrWarming = c.depth_ok || warmingUp;
  const volOkOrWarming = c.volume_ok || warmingUp;

  // dynamic rule labels — live from config thresholds, not hardcoded
  const hardCap = th.spread_hard_cap ?? 1.40;
  const minDepth = th.min_depth_each_side_usd ?? 200;
  const band = th.depth_band_pct ?? 2.0;
  const volTarget = th.volume_target_daily_usd ?? c.volume_target ?? 1500;
  const volThresh = (th.volume_alert_threshold ?? c.volume_alert_threshold ?? 0.70) * 100;

  const rule1Label = `${t("spread")} < ${hardCap.toFixed(2)}%`;
  const rule2Label = `${t("depth")} ±${band.toFixed(1)}% > $${minDepth.toFixed(0)}`;
  const rule3Label = `${t("volume24h")} > $${volTarget.toLocaleString()} / 24h`;

  return (
    <div className="mq-panel" data-testid="compliance-panel">
      <div className="mq-panel-header">
        <span className="mq-heading text-sm font-semibold tracking-wider">
          {t("compliance")}
        </span>
        <div className="flex items-center gap-2">
          {warmingUp && (
            <span
              data-testid="compliance-warming-up-badge"
              className="mq-label px-2 py-0.5 border"
              style={{ borderColor: "var(--mq-yellow)", color: "var(--mq-yellow)" }}
            >
              {t("complianceWarmingUp")}
            </span>
          )}
          {c.aggressive_mode && (
            <span className="mq-label" style={{ color: "var(--mq-yellow)" }}>
              ⚡ {t("aggressiveMode")}
            </span>
          )}
        </div>
      </div>
      <div className="px-4">
        <Row
          testid="compliance-status-spread"
          label={rule1Label}
          ok={c.spread_ok}
          detail={`${spread.toFixed(3)}%`}
        />
        <Row
          testid="compliance-status-depth"
          label={rule2Label}
          ok={depthOkOrWarming}
          warn={!c.depth_ok && warmingUp}
          detail={`$${Math.round(dBid)} · $${Math.round(dAsk)}`}
        />
        <Row
          testid="compliance-status-volume"
          label={rule3Label}
          ok={volOkOrWarming}
          warn={!c.volume_ok && (warmingUp || (c.volume_pct ?? 0) >= (th.volume_alert_threshold ?? 0.70))}
          detail={`$${vol.toFixed(0)} / $${volTarget.toFixed(0)} · ${((c.volume_pct ?? 0) * 100).toFixed(0)}% · alert ${volThresh.toFixed(0)}%`}
        />
      </div>

      {/* Price Guard block (Phase 2) — visible only when guard is enabled */}
      {state?.price_guard?.enabled && (
        <div className="px-4 pt-2 pb-3 border-t mt-2" style={{ borderColor: "var(--mq-border)" }}
             data-testid="price-guard-block">
          <div className="flex items-center justify-between mb-1">
            <span className="mq-label" style={{ color: "var(--mq-cyan)" }}>{t("priceGuardTitle")}</span>
            <span data-testid="price-guard-zone"
                  className="mq-label px-2 py-0.5 border"
                  style={{
                    borderColor: (state.price_guard.zone || "").startsWith("BLOCK") || state.price_guard.zone === "FAIL"
                      ? "var(--mq-red)"
                      : (state.price_guard.zone || "").startsWith("WARN") ? "var(--mq-yellow)" : "var(--mq-green)",
                    color: (state.price_guard.zone || "").startsWith("BLOCK") || state.price_guard.zone === "FAIL"
                      ? "var(--mq-red)"
                      : (state.price_guard.zone || "").startsWith("WARN") ? "var(--mq-yellow)" : "var(--mq-green)",
                  }}>
              {state.price_guard.zone || "—"}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono">
            <div data-testid="pg-open"><span className="mq-label">{t("priceGuardOpen")}:</span> {state.price_guard.daily_open?.toFixed(6) || "—"}</div>
            <div data-testid="pg-source"><span className="mq-label">{t("priceGuardSource")}:</span> {state.price_guard.daily_open_source || "—"}</div>
            <div data-testid="pg-pct"><span className="mq-label">{t("priceGuardPct")}:</span> {state.price_guard.pct_from_open?.toFixed(3) || "0.000"}%</div>
            <div data-testid="pg-allowed-side"><span className="mq-label">{t("priceGuardAllowedSide")}:</span> {state.price_guard.allowed_side || "—"}</div>
            <div data-testid="pg-rate"><span className="mq-label">{t("priceGuardRate")}:</span> {state.price_guard.rate_pct_per_min?.toFixed(2) || "0.10"}%/min</div>
            <div data-testid="pg-mode"><span className="mq-label">{t("priceGuardMode")}:</span> {state.price_guard.dry_run ? "DRY-RUN" : "LIVE"}</div>
            {state.price_guard.reason && (
              <div className="col-span-2 text-[var(--mq-text-3)] truncate">{t("priceGuardReason")}: {state.price_guard.reason}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
